import type { DashboardMetricsResponse } from '../../../types/dashboard';
import { toDemographicCounts } from '../demographics';
import { collectGoalsByMetric } from '../goalsByMetric';
import { asNullableNumber } from '../metricSeriesUtils';
import type { TccData } from './types';
import { MAX_QUARTERS_CHART } from '../constants';

export function latest<T>(arr: T[]): T {
  return arr[arr.length - 1];
}

export function previous<T>(arr: T[]): T | null {
  return arr.length >= 2 ? arr[arr.length - 2] : null;
}

export function pctChange(current: number, prevVal: number | null): number | null {
  if (prevVal === null || prevVal === 0) return null;
  return ((current - prevVal) / prevVal) * 100;
}

const asNumber = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

export function mapApiToTccData(data: DashboardMetricsResponse | null | undefined): TccData | null {
  if (!data) return null;
  const section = data.sections.find((entry) => entry.sectionId === 'tcc');
  if (!section) return null;

  const byId = new Map(section.metrics.map((metric) => [metric.metricId, metric]));

  const participants = byId.get('tcc.participants');
  const graduates = byId.get('tcc.graduates');
  const multiple = byId.get('tcc.multipleCredentials');
  const awarded = byId.get('tcc.credentialsAwarded');
  const employment = byId.get('tcc.employment');

  return {
    participants: (participants?.series ?? []).slice(-MAX_QUARTERS_CHART).map((p) => ({
      quarter: String(p.quarter),
      participantsStarted: asNumber(p.participantsStarted),
      byRace: toDemographicCounts(p.byRace),
      byGender: toDemographicCounts(p.byGender),
      qoq: (p.qoq ?? {}) as Record<string, unknown>,
      yoy: (p.yoy ?? {}) as Record<string, unknown>,
    })) ?? [],
    participantsAllTimeTotal: participants ? asNumber(participants.allTime?.participantsStarted) : 0,
    participantsAllTimeByRace: toDemographicCounts(participants?.allTime?.byRace),
    participantsAllTimeByGender: toDemographicCounts(participants?.allTime?.byGender),
    graduates: (graduates?.series ?? []).slice(-MAX_QUARTERS_CHART).map((p) => ({
      quarter: String(p.quarter),
      graduates: asNumber(p.graduates),
      graduatesByRace: toDemographicCounts(p.graduatesByRace),
      graduatesByGender: toDemographicCounts(p.graduatesByGender),
      qoq: (p.qoq ?? {}) as Record<string, unknown>,
      yoy: (p.yoy ?? {}) as Record<string, unknown>,
    })) ?? [],
    graduatesAllTimeTotal: graduates ? asNumber(graduates.allTime?.graduates) : 0,
    graduatesAllTimeByRace: toDemographicCounts(graduates?.allTime?.graduatesByRace),
    graduatesAllTimeByGender: toDemographicCounts(graduates?.allTime?.graduatesByGender),
    multipleCredentials: (multiple?.series ?? []).slice(-MAX_QUARTERS_CHART).map((p) => ({
      quarter: String(p.quarter),
      multipleCredentialEarners: asNumber(p.multipleCredentialEarners),
      qoq: (p.qoq ?? {}) as Record<string, unknown>,
      yoy: (p.yoy ?? {}) as Record<string, unknown>,
    })) ?? [],
    multipleCredentialsAllTimeTotal: multiple ? asNumber(multiple.allTime?.multipleCredentialEarners) : 0,
    credentialsAwarded: (awarded?.series ?? []).slice(-MAX_QUARTERS_CHART).map((p) => ({
      quarter: String(p.quarter),
      credentialsAwarded: asNumber(p.credentialsAwarded),
      qoq: (p.qoq ?? {}) as Record<string, unknown>,
      yoy: (p.yoy ?? {}) as Record<string, unknown>,
    })) ?? [],
    credentialsAwardedAllTimeTotal: awarded ? asNumber(awarded.allTime?.credentialsAwarded) : 0,
    employment: (employment?.series ?? []).slice(-MAX_QUARTERS_CHART).map((p) => ({
      quarter: String(p.quarter),
      employedWithinYear: asNumber(p.employedWithinYear),
      employmentRate: asNullableNumber(p.employmentRate),
      qoq: (p.qoq ?? {}) as Record<string, unknown>,
      yoy: (p.yoy ?? {}) as Record<string, unknown>,
    })) ?? [],
    employmentAllTimeTotal: employment ? asNumber(employment.allTime?.employedWithinYear) : 0,
    goalsByMetric: collectGoalsByMetric(section.metrics),
  };
}
